import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { Download, X } from 'lucide-react';
import { devicePlatform, getInstallState, promptInstall, subscribeInstallPrompt, wasInstalledBefore } from '../../services/pwaInstall';
import { InstallSteps } from '../../pages/InstallAppPage';

const dismissedBefore = () => {
  try {
    return Boolean(localStorage.getItem('pwa-install-dismissed'));
  } catch {
    return false;
  }
};

const InstallPrompt = ({ className = '' }) => {
  const location = useLocation();
  const [installState, setInstallState] = useState(getInstallState);
  const [dismissed, setDismissed] = useState(dismissedBefore);
  const [showSteps, setShowSteps] = useState(false);
  const [busy, setBusy] = useState(false);
  const platform = devicePlatform();

  useEffect(() => {
    const unsubscribe = subscribeInstallPrompt(setInstallState);
    setInstallState(getInstallState());
    return unsubscribe;
  }, []);

  // The install page has its own, larger button.
  if (platform === 'desktop' || installState.installed || wasInstalledBefore()
    || dismissed || location.pathname === '/install') return null;

  const handleInstall = async () => {
    if (busy) return;
    if (!installState.canPrompt) { setShowSteps(true); return; }
    setBusy(true);
    const outcome = await promptInstall();
    setBusy(false);
    if (outcome !== 'accepted') setShowSteps(true);
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem('pwa-install-dismissed', '1');
    } catch {
      // Private windows can refuse storage; the banner simply returns next visit.
    }
  };

  return (
    <>
    <section aria-label="Install the Serviti app" className={`lg:hidden shrink-0 border-b border-blue-800 bg-blue-950 px-3 py-3 text-white ${className}`}>
      <div className="mx-auto flex max-w-2xl flex-wrap items-center gap-2">
        <img src="/serviti_app_icon_192.png" alt="" className="h-10 w-10 rounded-xl" />
        <div className="min-w-0 flex-1 basis-36">
          <p className="text-sm font-semibold">Serviti, one tap away</p>
          <p className="text-xs leading-5 text-blue-100">Your leads and chats on your home screen.</p>
        </div>
        <button
          type="button"
          onClick={handleInstall}
          disabled={busy}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-white px-3 text-sm font-semibold text-blue-950 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-60"
        >
          <Download size={16} aria-hidden="true" /> Install app
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss install banner"
          className="flex h-11 w-11 items-center justify-center rounded-full text-blue-100 hover:bg-blue-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>
    </section>
    <Dialog open={showSteps} onClose={() => setShowSteps(false)} className="fixed inset-0 z-[100]">
      <div className="fixed inset-0 bg-slate-950/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4">
        <Dialog.Panel className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
          <Dialog.Title className="text-xl font-bold text-gray-950">Install Serviti AI</Dialog.Title>
          <p className="mb-5 mt-2 text-sm leading-6 text-gray-600">Keep your leads, conversations and AI agent one tap away.</p>
          {installState.canPrompt ? (
            <button type="button" disabled={busy} onClick={handleInstall} className="min-h-[44px] w-full rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white">Install Serviti AI</button>
          ) : <InstallSteps platform={platform} />}
          <p className="mt-4 text-sm leading-6 text-gray-600">Opened inside WhatsApp or Instagram? Open this page in your phone’s browser first.</p>
          <button type="button" onClick={() => setShowSteps(false)} className="mt-5 min-h-[44px] w-full rounded-xl border border-gray-300 px-4 py-2 font-semibold text-gray-800">Done</button>
        </Dialog.Panel>
      </div>
    </Dialog>
    </>
  );
};

export default InstallPrompt;
